import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Lock, Eye, EyeOff, AlertCircle, ShieldCheck, RefreshCw, CheckCircle2, ArrowLeft } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_API_URL}/api`;

export default function ForgotPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep]       = useState(1); // 1=email, 2=otp, 3=new password
  const [email, setEmail]     = useState("");
  const [otp, setOtp]         = useState("");
  const [newPw, setNewPw]     = useState("");
  const [showPw, setShowPw]   = useState(false);
  const [tempToken, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [resending, setResending] = useState(false);

  const maskEmail = (e) => { if (!e) return ""; const [u, d] = e.split("@"); return `${u.slice(0,2)}***@${d}`; };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError(null); setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email });
      setToken(res.data.temp_token);
      setStep(2);
      toast.success(t("forgot.email.success"));
    } catch (err) { setError(err.response?.data?.detail || t("forgot.email.error")); }
    finally { setLoading(false); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { setError(t("forgot.otp.error")); return; }
    setError(null); setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-forgot-otp`, { temp_token: tempToken, otp });
      setStep(3);
    } catch (err) { setError(err.response?.data?.detail || t("forgot.otp.error")); }
    finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) { setError("Password minimal 8 karakter"); return; }
    setError(null); setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { temp_token: tempToken, new_password: newPw });
      toast.success(t("forgot.reset.success"));
      navigate("/login");
    } catch (err) { setError(err.response?.data?.detail || t("forgot.reset.error")); }
    finally { setLoading(false); }
  };

  const handleResend = async () => {
    setResending(true);
    try { await axios.post(`${API}/auth/resend-otp`, { temp_token: tempToken }); toast.success("OTP baru dikirim!"); setOtp(""); }
    catch { toast.error(t("forgot.otp.resendError")); } finally { setResending(false); }
  };

  const steps = [
    { label: t("forgot.step.email") },
    { label: t("forgot.step.verify") },
    { label: t("forgot.step.newPw") },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background" data-testid="forgot-password-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors" data-testid="back-to-login">
          <ArrowLeft className="w-3.5 h-3.5" /> {t("forgot.back")}
        </Link>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-8">
          {steps.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-mono font-bold border transition-colors ${
                i + 1 < step ? "bg-brand-primary border-brand-primary text-white" :
                i + 1 === step ? "border-brand-primary text-brand-primary" :
                "border-border text-muted-foreground"
              }`}>
                {i + 1 < step ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
              </div>
              <span className={`text-xs font-mono ${i + 1 === step ? "text-foreground" : "text-muted-foreground"}`}>{s.label}</span>
              {i < steps.length - 1 && <div className={`h-px w-6 ${i + 1 < step ? "bg-brand-primary" : "bg-border"}`} />}
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div key="s1" initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} className="space-y-6">
              <div>
                <h2 className="font-heading text-2xl font-bold">{t("forgot.title")}</h2>
                <p className="text-sm text-muted-foreground mt-1">{t("forgot.desc")}</p>
              </div>
              <form onSubmit={handleSendOtp} className="space-y-4">
                {error && (<div className="flex items-center gap-2 p-3 rounded-sm bg-red-500/10 border border-red-500/30" data-testid="forgot-error"><AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" /><p className="text-sm text-red-400">{error}</p></div>)}
                <div className="space-y-1.5">
                  <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required className="pl-9 rounded-sm text-sm" data-testid="forgot-email" />
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full rounded-md bg-brand-primary text-white" data-testid="send-otp-btn">
                  {loading ? t("forgot.email.sending") : t("forgot.email.submit")}
                </Button>
              </form>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="s2" initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} className="space-y-6">
              <div className="text-center">
                <div className="w-14 h-14 rounded-full bg-brand-primary/10 border border-brand-primary/20 flex items-center justify-center mx-auto mb-4">
                  <ShieldCheck className="w-7 h-7 text-brand-primary" />
                </div>
                <h2 className="font-heading text-2xl font-bold">{t("forgot.otp.title")}</h2>
                <p className="text-sm text-muted-foreground mt-1">Kode dikirim ke <span className="font-mono text-foreground">{maskEmail(email)}</span></p>
              </div>
              <form onSubmit={handleVerifyOtp} className="space-y-4">
                {error && (<div className="flex items-center gap-2 p-3 rounded-sm bg-red-500/10 border border-red-500/30" data-testid="forgot-otp-error"><AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" /><p className="text-sm text-red-400">{error}</p></div>)}
                <Input type="text" inputMode="numeric" maxLength={6} value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g,""))} placeholder="000000" required autoFocus className="rounded-sm text-center text-2xl font-mono tracking-widest h-14" data-testid="forgot-otp-input" />
                <Button type="submit" disabled={loading || otp.length !== 6} className="w-full rounded-md bg-brand-primary text-white" data-testid="verify-forgot-otp-btn">
                  {loading ? t("forgot.otp.verifying") : t("forgot.otp.submit")}
                </Button>
              </form>
              <div className="text-center">
                <button onClick={handleResend} disabled={resending} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5 mx-auto">
                  <RefreshCw className={`w-3.5 h-3.5 ${resending ? "animate-spin" : ""}`} />{resending ? t("forgot.otp.resending") : t("forgot.otp.resend")}
                </button>
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key="s3" initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} className="space-y-6">
              <div>
                <h2 className="font-heading text-2xl font-bold">Password Baru</h2>
                <p className="text-sm text-muted-foreground mt-1">{t("forgot.reset.desc")}</p>
              </div>
              <form onSubmit={handleResetPassword} className="space-y-4">
                {error && (<div className="flex items-center gap-2 p-3 rounded-sm bg-red-500/10 border border-red-500/30" data-testid="reset-error"><AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" /><p className="text-sm text-red-400">{error}</p></div>)}
                <div className="space-y-1.5">
                  <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Password Baru</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    <Input type={showPw ? "text" : "password"} value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="Min. 8 karakter" required minLength={8} className="pl-9 pr-9 rounded-sm text-sm" data-testid="new-password-input" />
                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showPw ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">Minimal 8 karakter</p>
                </div>
                <Button type="submit" disabled={loading} className="w-full rounded-md bg-brand-primary text-white" data-testid="reset-password-btn">
                  {loading ? t("forgot.reset.saving") : t("forgot.reset.submit")}
                </Button>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
